import { FC } from 'react';
import { format } from 'date-fns';
import { Tooltip } from 'react-tooltip';

import { dateFormat } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { Document } from 'types';
import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';
import {
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { statusBadgeMap } from '../../../constants/incentiveOfferOperationStatus';
import getOperationReference from '../../../utils/getOperationReference';
import {
  hasOperationPurchaseContract,
  hasOperationSellerSignature,
  resolveOperationDocumentId,
} from '../../../utils/operationDocsHelpers';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentiveOfferOperationsTableRowProps = {
  index: number;
  operation: IncentiveOfferOperation;
  isBuyOffer: boolean;
  onGoToOperation: (operationId: string) => void;
  onOpenGenerateContractModal?: (operation: IncentiveOfferOperation) => void;
};
/* eslint-enable no-unused-vars */

const getParticipantName = (
  participant: string | Partial<IncentivesMarketBeneficiary>
) => {
  if (typeof participant === 'string') return participant;
  return participant?.name || '';
};

const IncentiveOfferOperationsTableRow: FC<
  IncentiveOfferOperationsTableRowProps
> = ({
  index,
  operation,
  isBuyOffer,
  onGoToOperation,
  onOpenGenerateContractModal,
}) => {
  const { t } = useTranslate();

  const badge = statusBadgeMap[operation.status] || {
    className: 'badge-secondary',
    label: operation.status,
  };

  const hasSellerSignature = hasOperationSellerSignature(operation);
  const contractDocumentId = resolveOperationDocumentId(
    operation.incentivesMarketPurchaseContract
  );
  const hasPurchaseContract = hasOperationPurchaseContract(operation);
  const signatureTooltipId = `seller-signature-${operation._id}`;
  const signatureDisabledTooltipId = `seller-signature-disabled-${operation._id}`;
  const purchaseContractTooltipId = `purchase-contract-${operation._id}`;
  const purchaseContractGenerateTooltipId = `purchase-contract-generate-${operation._id}`;

  return (
    <tr
      style={{ cursor: 'pointer' }}
      onClick={() => onGoToOperation(operation._id)}
    >
      <td className="tb-tnx-id">
        <span className="ref text-base badge badge-light">
          {getOperationReference(operation)}
        </span>
      </td>
      <td>
        <span className="d-inline-flex align-items-center">
          <em className="icon ni ni-user mr-1 text-soft" />
          {getParticipantName(isBuyOffer ? operation.seller : operation.buyer)}
        </span>
      </td>
      <td>{formatNumber(operation.unitsCount)}</td>
      <td>
        <strong>{formatCurrencyDecimals(operation.totalPrice)}</strong>
      </td>
      <td>{format(new Date(operation.requestDate), dateFormat)}</td>
      <td className="text-center" onClick={(e) => e.stopPropagation()}>
        <div className="d-inline-flex align-items-center gap-1">
          {hasSellerSignature ? (
            <>
              <button
                type="button"
                className="btn btn-icon btn-trigger btn-sm"
                data-tooltip-id={signatureTooltipId}
                data-testid={`seller-signature-${operation._id}`}
              >
                <em className="icon ni ni-pen" />
              </button>
              <Tooltip
                id={signatureTooltipId}
                place="top"
                style={{ zIndex: 999 }}
              >
                <img
                  src={operation.sellerSignature}
                  alt={t('SignatureForContract')}
                  style={{ maxWidth: 200 }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <span
                className="btn btn-icon disabled"
                data-tooltip-id={signatureDisabledTooltipId}
                data-testid={`seller-signature-disabled-${operation._id}`}
              >
                <em className="icon ni ni-pen" />
              </span>
              <Tooltip
                id={signatureDisabledTooltipId}
                place="top"
                style={{ zIndex: 999 }}
              >
                {t('NoSignatureRegistered')}
              </Tooltip>
            </>
          )}

          {hasPurchaseContract ? (
            <DocumentsColumn
              index={index}
              documentsCount={1}
              documents={[{ _id: contractDocumentId }] as Document[]}
              tableView={false}
              hideOnMobile={false}
              tooltipId={purchaseContractTooltipId}
              tooltipText={t('IncentivesMarketPurchaseContractShort')}
            />
          ) : (
            <>
              <button
                type="button"
                className="btn btn-icon"
                data-tooltip-id={purchaseContractGenerateTooltipId}
                data-testid={`generate-purchase-contract-${operation._id}`}
                onClick={() => onOpenGenerateContractModal?.(operation)}
              >
                <em className="icon ni ni-file-download text-light" />
              </button>
              <Tooltip
                id={purchaseContractGenerateTooltipId}
                place="top"
                style={{ zIndex: 999 }}
              >
                {t('NoPurchaseContractGeneratePrompt')}
              </Tooltip>
            </>
          )}
        </div>
      </td>
      <td>
        <span className={`badge badge-dim ${badge.className}`}>
          {t(badge.label)}
        </span>
      </td>
      <td className="tb-tnx-action text-right">
        <button
          type="button"
          className="btn btn-icon btn-trigger btn-sm"
          title={t('ViewDetail')}
          onClick={(e) => {
            e.stopPropagation();
            onGoToOperation(operation._id);
          }}
        >
          <em className="icon ni ni-arrow-right" />
        </button>
      </td>
    </tr>
  );
};

export default IncentiveOfferOperationsTableRow;
